/**
 * Capture the current page as a downsized JPEG dataURL suitable for
 * attaching to bug reports. Uses html2canvas at the DOM level (no
 * screen-capture permission prompt). The SmeFeedbackOverlay caller
 * hides its modal before calling so it doesn't occlude the capture.
 */
import html2canvas from 'html2canvas'

export const SCREENSHOT_MAX_WIDTH = 1024
export const SCREENSHOT_JPEG_QUALITY = 0.7
export const SCREENSHOT_MAX_BYTES = 512_000

export type CapturedScreenshot = {
  data_url: string
  width: number
  height: number
  bytes: number
  captured_at: string
}

export async function captureDownsizedScreenshot(target: HTMLElement = document.documentElement): Promise<CapturedScreenshot> {
  const viewportWidth = Math.max(window.innerWidth || target.clientWidth || 1, 1)
  const viewportHeight = Math.max(window.innerHeight || target.clientHeight || 1, 1)
  const viewportX = Math.max(window.scrollX || window.pageXOffset || 0, 0)
  const viewportY = Math.max(window.scrollY || window.pageYOffset || 0, 0)

  const canvas = await html2canvas(target, {
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    width: viewportWidth,
    height: viewportHeight,
    windowWidth: viewportWidth,
    windowHeight: viewportHeight,
    x: viewportX,
    y: viewportY,
    scrollX: viewportX,
    scrollY: viewportY,
    // Skip elements marked to opt out (e.g. the overlay modal itself)
    ignoreElements: (el) => el.getAttribute('data-screenshot-ignore') === 'true',
  })

  const scale = canvas.width > SCREENSHOT_MAX_WIDTH ? SCREENSHOT_MAX_WIDTH / canvas.width : 1
  const outW = Math.round(canvas.width * scale)
  const outH = Math.round(canvas.height * scale)

  const scaled = document.createElement('canvas')
  scaled.width = outW
  scaled.height = outH
  const ctx = scaled.getContext('2d')
  if (!ctx) throw new Error('Canvas 2D context unavailable')
  ctx.drawImage(canvas, 0, 0, outW, outH)

  const dataUrl = scaled.toDataURL('image/jpeg', SCREENSHOT_JPEG_QUALITY)
  // Approximate byte size of the decoded payload. base64 ≈ 4/3 of bytes.
  const bytes = Math.ceil((dataUrl.length - 'data:image/jpeg;base64,'.length) * 3 / 4)

  if (bytes > SCREENSHOT_MAX_BYTES) {
    throw new Error(`Screenshot too large after downsizing (${bytes} bytes > ${SCREENSHOT_MAX_BYTES}). Try capturing less of the page.`)
  }

  return {
    data_url: dataUrl,
    width: outW,
    height: outH,
    bytes,
    captured_at: new Date().toISOString(),
  }
}
